import { Suspense, lazy, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { PartialRouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import Employee from './content/employee';
import { Login } from './content/login';
import { ResetPasswordForm } from './content/resetPassword';
import ShadowResources from './content/shadowResources';
import EmployeeEditForm from './content/employee/EmployeeEditForm';
import RoleManagement from './content/roleManagement';
import ProjectDetails from './content/projectDetails';
import ProjectProfile from './content/projectDetails/ProjectProfile';
import ProjectEditForm from './content/projectDetails/ProjectEditForm';
import YearPlan from './content/yearPlan';
import ProfilePage from './content/profile/ProfilePage';
import { RequireAuth } from './components/RequireAuth';
import Dashboard from './content/dashboards';
import ActualHoursTable from './content/dashboards/ActualHoursTable';
import WeeklyTimeSheetTable from './content/dashboards/WeeklyTimeSheetTable';
import WeeklyProjectAllocation from './content/dashboards/Cards/components/WeeklyProjectAllocation';
import ProjectDeliveryStatus from './content/dashboards/Cards/components/ProjectDeliveryStatus';
import ProjectStatus from './content/dashboards/Cards/components/ProjectStatus';
import ReportSheet from './content/dashboards/Cards/components/ReportSheet';
import WeeklyResourceAllocation from './content/dashboards/Cards/components/WeeklyResourceAllocation';
import SowPo from './content/dashboards/Cards/components/SowPo';
import ProgressStatus from './content/dashboards/Cards/components/ProgressStatus';
import ProjectResource from './content/dashboards/Cards/components/ProjectResource';
import ResourceMonthlyHours from './content/dashboards/Cards/components/ResourceMonthlyHours';
import Skills from './content/skills';
import SingleSkillView from './content/skills/SingleSkillView';

import ResourceSinglePage from './content/shadowResources/components/ResourceSinglePage';
import ProjectQuickAccess from './content/dashboards/Cards/components/ProjectQuickAccess';
import QuickAccessTable from './content/dashboards/Cards/components/QuickAccessTable';
import ProjectHealth from './content/projectHealth';
import Segments from './content/segments';
import Configurations from './content/configurations';
import Customers from './content/clients';
import Stakeholders from './content/stakeholders';
import ProjectCreatePage from './content/projectDetails/ProjectCreatePage';
import TeamsTable from './content/projectDetails/components/Teams/TeamsTable';
import TotalResourceAllocation from './content/dashboards/Cards/components/TotalResourceAllocation';
import DefineLeaves from './content/defineLeaves';
import LeaveManagement from './content/leaveManagement';
import ViewLeave from './content/leaveManagement/ViewLeave';
import HolidayCalender from './content/holidayCalender';
import Leave from './content/leaveManagement/Leave';
import LieuLeave from './content/leaveManagement/LieuLeave';
import RmPendingRequest from './content/leaveManagement/RmPendingRequest';
import LeaveHistory from './content/leaveManagement/LeaveHistory';
import CarryForwardedLeaves from './content/carryForwardedLeaves';
import EmployeeLeaves from './content/leaveManagement/EmployeeLeaves';
import ResourcePool from './content/resourcePool';
import WorkModel from './content/workModel';
import WorkFromHomeOld from './content/leaveManagement/WorkFromHome';
import WorkFromHome from './content/workFromHome';
import TaskManagement from './content/taskManagement';
import TimeEntry from './content/timeEntry';
import Attendance from './content/attendance/index';
import TaskAssignment from './content/taskAssignment';
import DetailedReport from './content/timsheetReports/components/detailedReport';
import SummarizedReport from './content/timsheetReports/components/summarizedReport';
import TaskBasedReport from './content/timsheetReports/components/taskBasedReport';
import Feedback from './content/Feedback';
import VarianceReport from './content/timsheetReports/components/varianceReport';
import DesignationView from 'src/content/Designation/DesignationView'; 
import  EmployeeListTable from 'src/content/timeSheetSuperUser/EmployeeListTable';
import EventManagement from './content/event';
import EmployeeOnboarding from './content/employeeOnboarding';
import ToDoListPage from 'src/content/employeeOnboarding/PreOnboarding/ToDoListPage';




const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const Status401 = Loader(
  lazy(() => import('src/content/pages/Status/Status401'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

export const routes: PartialRouteObject[] = [
  {
    path: '*',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Login />
      },
      {
        path: '/reset-password/:email/:token*',
        element: <ResetPasswordForm />
      },
      {
        path: 'login',
        element: <Navigate to="/" replace />
      },
      {
        path: '/unauthorized/:token',
        element: <Status401 />
      },
      {
        path: 'status',
        children: [
          {
            path: '/',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: '401',
            element: <Status401 />
          },
          {
            path: ' ',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboard',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Dashboard />
      },
      {
        path: 'actual-hours',
        element: (
          <RequireAuth tabname="" childCompoenent={<ActualHoursTable />} />
        )
      },
      {
        path: 'weekly-timesheet',
        element: (
          <RequireAuth tabname="" childCompoenent={<WeeklyTimeSheetTable />} />
        )
      },
      {
        path: 'weekly-project-allocation',
        element: (
          <RequireAuth
            tabname=""
            childCompoenent={<WeeklyProjectAllocation />}
          />
        )
      },
      {
        path: 'project-delivery-status',
        element: (
          <RequireAuth tabname="" childCompoenent={<ProjectDeliveryStatus />} />
        )
      },
      {
        path: 'progress-status',
        element: <RequireAuth tabname="" childCompoenent={<ProgressStatus />} />
      },
      {
        path: 'project-status',
        element: <RequireAuth tabname="" childCompoenent={<ProjectStatus />} />
      },
      {
        path: 'report-sheet',
        element: <RequireAuth tabname="" childCompoenent={<ReportSheet />} />
      },
      {
        path: 'weekly-resource-allocation',
        element: (
          <RequireAuth
            tabname=""
            childCompoenent={<WeeklyResourceAllocation />}
          />
        )
      },
      {
        path: 'sow-po-other',
        element: <RequireAuth tabname="" childCompoenent={<SowPo />} />
      },
      {
        path: 'project-resource',
        element: (
          <RequireAuth tabname="" childCompoenent={<ProjectResource />} />
        )
      },
      {
        path: 'resource-monthly-hours',
        element: (
          <RequireAuth tabname="" childCompoenent={<ResourceMonthlyHours />} />
        )
      },
      {
        path: 'project-quick-access',
        element: (
          <RequireAuth tabname="" childCompoenent={<ProjectQuickAccess />} />
        )
      },
      {
        path: 'quick-access-table/:code',
        element: (
          <RequireAuth tabname="" childCompoenent={<QuickAccessTable />} />
        )
      },
      {
        path: 'total-resource-allocation',
        element: (
          <RequireAuth
            tabname=""
            childCompoenent={<TotalResourceAllocation />}
          />
        )
      }
    ]
  },
  {
    path: 'employees',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="Employee" childCompoenent={<Employee />} />
        )
      },
      {
        path: 'edit-employee/:employeeId',
        element: (
          <RequireAuth
            tabname="Employee"
            childCompoenent={<EmployeeEditForm />}
          />
        )
      }
    ]
  },
  {
    path: 'skills',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <RequireAuth tabname="Skills" childCompoenent={<Skills />} />
      },
      {
        path: '/single-skill/:skillName',
        element: (
          <RequireAuth tabname="Skill" childCompoenent={<SingleSkillView />} />
        )
      }
    ]
  },
  {
    path: 'project-details',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <RequireAuth tabname="" childCompoenent={<ProjectDetails />} />
      },
      {
        path: '/:customerId',
        element: <RequireAuth tabname="" childCompoenent={<ProjectDetails />} />
      },
      {
        path: 'project-profile/:projectCode',
        element: <RequireAuth tabname="" childCompoenent={<ProjectProfile />} />
      },
      {
        path: 'project-edit/:projectCode',
        element: (
          <RequireAuth tabname="" childCompoenent={<ProjectEditForm />} />
        )
      },
      {
        path: 'create-new',
        element: (
          <RequireAuth tabname="" childCompoenent={<ProjectCreatePage />} />
        )
      },
      {
        path: 'teams/:projectCode',
        element: <RequireAuth tabname="" childCompoenent={<TeamsTable />} />
      }
    ]
  },
  {
    path: 'resource-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <ShadowResources />
      },
      {
        path: 'resource-edit/:employeeId',
        element: (
          <RequireAuth tabname="" childCompoenent={<ResourceSinglePage />} />
        )
      }
    ]
  },
  {
    path: 'resource-pool',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <RequireAuth tabname="" childCompoenent={<ResourcePool />} />
      },
      {
        path: '/:type',
        element: <RequireAuth tabname="" childCompoenent={<ResourcePool />} />
      }
    ]
  },
  {
    path: 'role-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <RequireAuth tabname="" childCompoenent={<RoleManagement />} />
      }
    ]
  },
  {
    path: 'year-plan',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <RequireAuth tabname="" childCompoenent={<YearPlan />} />
      }
    ]
  },
  {
    path: 'holiday-calender',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <RequireAuth tabname="" childCompoenent={<HolidayCalender />} />
      }
    ]
  },
  {
    path: 'profile',
    element: <SidebarLayout />,
    children: [
      {
        path: 'details/:employeeId',
        element: <ProfilePage />
      }
    ]
  },
  {
    path: 'segments',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Segments />
      }
    ]
  },
  {
    path: 'clients',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Customers />
      }
    ]
  },
  {
    path: 'stakeholders',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Stakeholders />
      }
    ]
  },
  {
    path: 'project-health',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <ProjectHealth />
      }
    ]
  },
  {
    path: 'configurations',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Configurations />
      }
    ]
  },
  {
    path: 'define-leaves',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <DefineLeaves/>
      }
    ]
  },
  {
    path: 'leave-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <LeaveManagement/>,
      },
      {
        path: 'leave-view/:id',
        element: (
          <RequireAuth tabname="" childCompoenent={<ViewLeave />} />
        )
      },
      {
        path: 'apply-leave',
        element: (
          <RequireAuth tabname="" childCompoenent={<Leave />} />
        )
      },
      {
        path: 'apply-lieu-leave',
        element: (
          <RequireAuth tabname="" childCompoenent={<LieuLeave />} />
        )
      },
      {
        path: 'RM-pending-requests',
        element: (
          <RequireAuth tabname="" childCompoenent={<RmPendingRequest />} />
        )
      },
      {
        path: 'my-leave-history/:id',
        element: (
          <RequireAuth tabname="" childCompoenent={<LeaveHistory />} />
        )
      },
      {
        path: 'employee-leaves',
        element: (
          <RequireAuth tabname="" childCompoenent={<EmployeeLeaves />} />
        )
      },
      {
        path: 'apply-wfh',
        element: (
          <RequireAuth tabname="" childCompoenent={<WorkFromHomeOld />} />
        )
      }
    ]
  },
  {
    path: 'work-model',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <WorkModel />
      }
    ]
  },
  {
    path: 'work-from-home',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="" childCompoenent={<WorkFromHome />} />
        )
      },
      {
        path: 'apply',
        element: (
          <RequireAuth tabname="" childCompoenent={<WorkFromHome />} />
        )
      },
      {
        path: 'apply/:requestId',
        element: (
          <RequireAuth tabname="" childCompoenent={<WorkFromHome />} />
        )
      },
      {
        path: 'requests',
        element: (
          <RequireAuth tabname="" childCompoenent={<WorkFromHome />} />
        )
      }
    ]
  },
  {
  path: 'attendance',
  element: <SidebarLayout />,
  children: [
    {
      path: '/',
      element: <RequireAuth tabname="" childCompoenent={<Attendance />} />
    }
  ]
},
{
  path: 'time-entry',
  element: <SidebarLayout />,
  children: [
    {
      path: '/', // for /time-entry
      element: (
        <RequireAuth tabname="" childCompoenent={<TimeEntry />} />
      )
    }
  ]
},

  {
    path: 'activity-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="" childCompoenent={<TaskManagement />} />
        )
      }
    ]
  },
  {
    path: 'task-assignment',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <RequireAuth tabname="" childCompoenent={<TaskAssignment />} />
        )
      },
      {
        path: ':projectId/:taskAssignId/:employeeId',
        element: (
          <RequireAuth tabname="" childCompoenent={<TaskAssignment />} />
        )
      },
    ]
  },
  {
    path: 'carry-forwarded-leaves',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <CarryForwardedLeaves />
      }
    ]
  },
  {
    path: 'detailed-report',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="" childCompoenent={<DetailedReport />} />
        )
      },
    ]
  },
  {
    path: 'summarized-report',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="" childCompoenent={<SummarizedReport />} />
        )
      },
    ]
  },
  {
    path: 'taskBased-report',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="" childCompoenent={<TaskBasedReport />} />
        )
      },
    ]
  },
  {
    path: 'feedback',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Feedback />
      }
    ]
  },
  {
    path: 'event-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <EventManagement />
      }
    ]
  },
   {
    path: 'employee-onboarding',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <EmployeeOnboarding />
      }
    ]
  },
  {
    path: 'variance-report',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: (
          <RequireAuth tabname="" childCompoenent={<VarianceReport />} />
        )
      },
    ]
  },
  {
    path: 'designations',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <DesignationView />
      }
    ]
  },
  {
    path: 'superuserTimeSheet',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <EmployeeListTable />
      }
    ]
  },
  {
  path: 'employee-time-entry',
  element: <SidebarLayout />,
  children: [
    {
      path: ':id', 
      element: (
        <RequireAuth tabname="" childCompoenent={<TimeEntry />} />
      )
    }
  ]
},
{
  path: 'employee-onboarding',
  element: <SidebarLayout />,
  children: [
    {
      path: '',
      element: <EmployeeOnboarding />
    },
    {
      path: 'todo/:candidateId',
      element: (
        <RequireAuth tabname="" childCompoenent={<ToDoListPage />} />
      )
    }
  ]
}

  
];
